import type { AppLang } from './settings';
import { useSettings } from './settings';

const en = {
  tagline: 'Hear every comment on your LIVE.',
  intro: 'Type a TikTok username. BacaChat reads chats, gifts and follows out loud the moment they arrive.',
  usernameLabel: 'TikTok username',
  usernamePlaceholder: 'username or LIVE link',
  start: 'Start listening',
  mustBeLive: 'The account has to be live right now.',
  voiceLanguage: 'Voice language',
  readAloud: 'Read comments aloud',
  soundFor: 'Sound for gifts & follows',
  recent: 'Recent',
  slotsFree: '{n} of {max} slots free',
  slotsNone: 'All {max} slots busy',
  slotsLoading: 'Checking free slots…',
  invalidUsername: 'That does not look like a TikTok username.',
  language: 'Language',
  appLanguage: 'App language',
  detectLanguage: "Detect each comment's language",
  detectLanguageSub: 'Switches voice when someone writes in English or 中文',
  voice: 'Voice',
  bestVoice: 'Best available voice',
  noVoices: 'No voice for this language on this device. Windows: Settings › Time & language › Speech › Add voices. Chrome and Edge ship extra voices too.',
  speed: 'Speed',
  pitch: 'Pitch',
  volume: 'Volume',
  normal: 'Normal',
  low: 'Low',
  high: 'High',
  testVoice: 'Test voice',
  testSentence: 'Hello, this is how comments will sound.',
  readSection: 'Read aloud',
  chatComments: 'Chat comments',
  gifts: 'Gifts',
  newFollows: 'New follows',
  shares: 'Shares',
  viewersJoining: 'Viewers joining',
  likes: 'Likes',
  formatLabel: 'How a comment is read',
  formatHelp: 'Use {comment} for the message and {nickname} for who wrote it, e.g. {nickname} says {comment}.',
  soundEffects: 'Sound effects',
  sfxGift: 'Gift',
  sfxFollow: 'Follow',
  sfxShare: 'Share',
  sfxChat: 'New chat (before reading)',
  sfxNames: { coin: 'Coin chime', bell: 'Bell', pop: 'Pop', whoosh: 'Whoosh', tick: 'Tick', off: 'Off' },
  preview: 'Preview {name} sound',
  filters: 'Filters',
  skipEmoji: 'Skip emoji-only comments',
  skipLinks: 'Skip comments with links',
  dedupe: "Don't repeat the same comment",
  maxLength: 'Longest comment to read',
  characters: '{n} characters',
  noLimit: 'No limit',
  blocklist: 'Never read these words',
  blocklistPlaceholder: 'Separate words with commas',
  prefix: 'Only read comments starting with',
  prefixPlaceholder: 'Optional, e.g. !',
  queue: 'Queue',
  queueMax: 'Most comments waiting to be read',
  queueOption: '{n} — then skip the oldest',
  queueHelp: "Busy LIVEs move faster than a voice can read. A short queue keeps what you hear close to what's on screen.",
  overlay: 'OBS overlay',
  overlayHelp:
    'Add this URL as a Browser Source in OBS or TikTok Live Studio for a transparent chat feed. It is silent: to put the voice in your stream, capture this browser tab as an audio source.',
  copy: 'Copy',
  copied: 'Copied',
  back: 'Back',
  settings: 'Voice & sound',
  bigText: 'Big-text reader',
  compact: 'Back to compact view',
  stopAndBack: 'Stop and go back',
  viewers: 'Viewers',
  follows: 'Follows',
  readingAloud: 'READING ALOUD',
  paused: 'PAUSED',
  waiting: 'WAITING FOR CHAT',
  voiceOff: 'VOICE OFF',
  inQueue: '{n} in queue',
  pause: 'Pause',
  resume: 'Resume',
  skip: 'Skip',
  readAgain: 'Read again',
  smaller: 'Smaller text',
  bigger: 'Bigger text',
  voiceOn: 'Voice on',
  voiceOffShort: 'Voice off',
  soundsOn: 'Sounds on',
  soundsOff: 'Sounds off',
  all: 'All',
  chats: 'Chats',
  giftsLikes: 'Gifts & likes',
  followsTab: 'Follows',
  upNext: 'Up next',
  justRead: 'Just read',
  watching: 'watching',
  nothingYet: 'Waiting for the first comment…',
  connecting: 'Connecting…',
  connected: 'Connected',
  offline: 'Not live',
  ended: 'LIVE ended',
  live: 'LIVE',
  statusOffline: '{user} is not live right now. Start the LIVE first, then try again.',
  statusEnded: 'The LIVE has ended.',
  statusKicked: 'Your slot was given to a priority streamer. BacaChat will try again automatically.',
  statusFull: 'All {max} slots are busy right now. Retrying in {s}s…',
  statusBusy: 'BacaChat is busy. Retrying in {s}s…',
  statusIpLimit: 'Too many streams from this connection (max {max}).',
  statusError: 'Something went wrong: {message}',
  statusReconnecting: 'Connection dropped. Reconnecting in {s}s…',
  retryNow: 'Retry now',
  tryAgain: 'Try again',
  giftSpeech: '{nickname} sent {count} {gift}',
  followSpeech: '{nickname} followed you',
  shareSpeech: '{nickname} shared your LIVE',
  joinSpeech: '{nickname} joined',
  likeSpeech: '{nickname} sent {count} likes',
  giftText: 'sent {gift} ×{count}',
  followText: 'followed the host',
  shareText: 'shared the LIVE',
  joinText: 'joined the LIVE',
  joinManyText: '{n} viewers joined',
  likeText: 'sent {count} likes',
  tagGift: 'GIFT',
  tagFollow: 'FOLLOW',
  tagShare: 'SHARE',
  tagLike: 'LIKE',
  tagJoin: 'JOIN',
  overlayUsesSlot: 'Note: the overlay uses one of the slots too.',
  deviceVoices: 'Device voices',
  more: 'More…',
  autoUnlock: 'Tap anywhere once so the browser allows sound.',
  changeVoiceLanguage: 'Change voice language',
  speakingIn: 'Speaking in {lang}',
  detectedLanguage: 'Detected from the comment',
  edgeHint: 'For a natural {lang} voice, open BacaChat in Microsoft Edge. It has free neural voices that sound close to a real person.',
  dismiss: 'Dismiss',
  speedUpBusy: 'Read faster when busy',
  speedUpBusySub: 'Speeds up by as much as 30% when comments pile up',
  premiumVoice: 'Premium voice (ElevenLabs)',
  premiumVoiceLeft: '{n} credits left this month',
  premiumVoiceUsedUp: "Used up for now, so this device's voice is reading.",
  premiumVoiceNote: 'Only for priority streamers. Falls back to this device automatically.',
  restricted: 'Restricted LIVE',
  statusLoginExpired: "{user}'s LIVE is 18+ restricted, and BacaChat's TikTok login has expired, so it cannot open it right now. Ask the BacaChat admin to renew the login, or turn off the age restriction in the LIVE settings.",
  killfeed: 'Comment banner (killfeed)',
  killfeedHelp: 'Shows the newest comment with avatar and name for a few seconds, transparent otherwise. In TikTok LIVE Studio or OBS, add it as a link or browser source with a custom resolution of 560 × 200. Add &demo=1 to preview it before you go live.',
  retryingIn: 'Checking again in {s}s (try {n} of {max}).',
  retryGaveUp: 'Stopped checking after {max} tries. Tap Try again once the LIVE starts.',
  statusOpeningRestricted: "{user}'s LIVE is 18+ restricted. Opening it with BacaChat's TikTok login…",
  statusRestricted: '{user} is live, but the LIVE is limited to 18+ or logged-in viewers, so BacaChat cannot read it. Turn off the age restriction in the LIVE settings, then try again.',
  statusRestrictedLoggedIn: "{user} is live with an 18+ restriction, and BacaChat's TikTok login could not open it. Turn off the age restriction in the LIVE settings, then try again.",
};

export type Strings = typeof en;

const ms: Strings = {
  ...en,
  tagline: 'Dengar setiap komen LIVE anda.',
  intro: 'Taip username TikTok. BacaChat bacakan chat, gift dan follow sebaik sahaja ia masuk.',
  usernameLabel: 'Username TikTok',
  usernamePlaceholder: 'username atau link LIVE',
  start: 'Mula dengar',
  mustBeLive: 'Akaun itu mesti sedang LIVE sekarang.',
  voiceLanguage: 'Bahasa suara',
  readAloud: 'Bacakan komen',
  soundFor: 'Bunyi untuk gift & follow',
  recent: 'Terkini',
  slotsFree: '{n} daripada {max} slot kosong',
  slotsNone: 'Semua {max} slot penuh',
  slotsLoading: 'Menyemak slot kosong…',
  invalidUsername: 'Itu tidak nampak seperti username TikTok.',
  language: 'Bahasa',
  appLanguage: 'Bahasa aplikasi',
  detectLanguage: 'Kesan bahasa setiap komen',
  detectLanguageSub: 'Tukar suara bila ada yang tulis dalam English atau 中文',
  voice: 'Suara',
  bestVoice: 'Suara terbaik yang ada',
  noVoices: 'Tiada suara untuk bahasa ini pada peranti ini. Windows: Settings › Time & language › Speech › Add voices. Chrome dan Edge ada suara tambahan juga.',
  speed: 'Kelajuan',
  pitch: 'Nada',
  volume: 'Kelantangan',
  normal: 'Biasa',
  low: 'Rendah',
  high: 'Tinggi',
  testVoice: 'Uji suara',
  testSentence: 'Hai, macam inilah bunyi komen nanti.',
  readSection: 'Bacakan',
  chatComments: 'Komen chat',
  gifts: 'Gift',
  newFollows: 'Follower baru',
  shares: 'Share',
  viewersJoining: 'Penonton masuk',
  likes: 'Like',
  formatLabel: 'Cara komen dibaca',
  formatHelp: 'Guna {comment} untuk mesej dan {nickname} untuk nama penulis, cth. {nickname} cakap {comment}.',
  soundEffects: 'Kesan bunyi',
  sfxGift: 'Gift',
  sfxFollow: 'Follow',
  sfxShare: 'Share',
  sfxChat: 'Chat baru (sebelum dibaca)',
  sfxNames: { coin: 'Bunyi syiling', bell: 'Loceng', pop: 'Pop', whoosh: 'Whoosh', tick: 'Tik', off: 'Tutup' },
  preview: 'Dengar bunyi {name}',
  filters: 'Tapisan',
  skipEmoji: 'Langkau komen emoji sahaja',
  skipLinks: 'Langkau komen yang ada link',
  dedupe: 'Jangan ulang komen yang sama',
  maxLength: 'Komen terpanjang untuk dibaca',
  characters: '{n} aksara',
  noLimit: 'Tiada had',
  blocklist: 'Jangan baca perkataan ini',
  blocklistPlaceholder: 'Asingkan dengan koma',
  prefix: 'Baca komen yang bermula dengan',
  prefixPlaceholder: 'Pilihan, cth. !',
  queue: 'Giliran',
  queueMax: 'Komen maksimum menunggu dibaca',
  queueOption: '{n} — kemudian langkau yang paling lama',
  queueHelp: 'LIVE yang sibuk lebih laju daripada suara boleh baca. Giliran pendek pastikan apa yang didengar dekat dengan apa di skrin.',
  overlay: 'Overlay OBS',
  overlayHelp:
    'Tambah URL ini sebagai Browser Source dalam OBS atau TikTok Live Studio untuk paparan chat lut sinar. Ia senyap: untuk masukkan suara ke dalam stream, capture tab browser ini sebagai sumber audio.',
  copy: 'Salin',
  copied: 'Disalin',
  back: 'Kembali',
  settings: 'Suara & bunyi',
  bigText: 'Pembaca teks besar',
  compact: 'Kembali ke paparan padat',
  stopAndBack: 'Berhenti dan kembali',
  viewers: 'Penonton',
  follows: 'Follow',
  readingAloud: 'SEDANG DIBACA',
  paused: 'DIJEDA',
  waiting: 'MENUNGGU CHAT',
  voiceOff: 'SUARA TUTUP',
  inQueue: '{n} menunggu',
  pause: 'Jeda',
  resume: 'Sambung',
  skip: 'Langkau',
  readAgain: 'Baca semula',
  smaller: 'Teks lebih kecil',
  bigger: 'Teks lebih besar',
  voiceOn: 'Suara buka',
  voiceOffShort: 'Suara tutup',
  soundsOn: 'Bunyi buka',
  soundsOff: 'Bunyi tutup',
  all: 'Semua',
  chats: 'Chat',
  giftsLikes: 'Gift & like',
  followsTab: 'Follow',
  upNext: 'Seterusnya',
  justRead: 'Baru dibaca',
  watching: 'menonton',
  nothingYet: 'Menunggu komen pertama…',
  connecting: 'Menyambung…',
  connected: 'Bersambung',
  offline: 'Tidak LIVE',
  ended: 'LIVE tamat',
  statusOffline: '{user} tidak LIVE sekarang. Mulakan LIVE dahulu, kemudian cuba lagi.',
  statusEnded: 'LIVE telah tamat.',
  statusKicked: 'Slot anda diberikan kepada streamer keutamaan. BacaChat akan cuba lagi secara automatik.',
  statusFull: 'Semua {max} slot sedang digunakan. Cuba lagi dalam {s}s…',
  statusBusy: 'BacaChat sibuk. Cuba lagi dalam {s}s…',
  statusIpLimit: 'Terlalu banyak stream dari sambungan ini (maksimum {max}).',
  statusError: 'Ada masalah: {message}',
  statusReconnecting: 'Sambungan terputus. Menyambung semula dalam {s}s…',
  retryNow: 'Cuba sekarang',
  tryAgain: 'Cuba lagi',
  giftSpeech: '{nickname} hantar {count} {gift}',
  followSpeech: '{nickname} dah follow',
  shareSpeech: '{nickname} share LIVE anda',
  joinSpeech: '{nickname} masuk',
  likeSpeech: '{nickname} hantar {count} like',
  giftText: 'hantar {gift} ×{count}',
  followText: 'follow host',
  shareText: 'share LIVE ini',
  joinText: 'masuk LIVE',
  joinManyText: '{n} penonton masuk',
  likeText: 'hantar {count} like',
  overlayUsesSlot: 'Nota: overlay juga guna satu slot.',
  deviceVoices: 'Suara peranti',
  more: 'Lagi…',
  autoUnlock: 'Ketik mana-mana sekali supaya browser benarkan bunyi.',
  changeVoiceLanguage: 'Tukar bahasa suara',
  speakingIn: 'Dibaca dalam {lang}',
  detectedLanguage: 'Dikesan daripada komen',
  edgeHint: 'Untuk suara {lang} yang lebih asli, buka BacaChat dalam Microsoft Edge. Ia ada suara neural percuma yang bunyinya hampir seperti orang sebenar.',
  dismiss: 'Tutup',
  speedUpBusy: 'Baca lebih laju bila sibuk',
  speedUpBusySub: 'Laju sehingga 30% bila komen bertimbun',
  premiumVoice: 'Suara premium (ElevenLabs)',
  premiumVoiceLeft: '{n} kredit lagi bulan ini',
  premiumVoiceUsedUp: 'Sudah habis buat masa ini, jadi suara peranti yang membaca.',
  premiumVoiceNote: 'Hanya untuk streamer keutamaan. Tukar ke suara peranti secara automatik.',
  restricted: 'LIVE terhad',
  statusLoginExpired: 'LIVE {user} disekat 18+, dan log masuk TikTok BacaChat sudah tamat tempoh, jadi ia tidak dapat dibuka sekarang. Minta admin BacaChat memperbaharui log masuk, atau matikan sekatan umur dalam tetapan LIVE.',
  killfeed: 'Banner komen (killfeed)',
  killfeedHelp: 'Tunjuk komen terbaru dengan gambar profil dan nama selama beberapa saat, lut sinar selebihnya. Dalam TikTok LIVE Studio atau OBS, tambah sebagai sumber link atau browser dengan resolusi tersuai 560 × 200. Tambah &demo=1 untuk pratonton sebelum LIVE.',
  retryingIn: 'Semak semula dalam {s}s (cubaan {n} daripada {max}).',
  retryGaveUp: 'Berhenti menyemak selepas {max} cubaan. Tekan Cuba lagi bila LIVE bermula.',
  statusOpeningRestricted: 'LIVE {user} disekat 18+. Membukanya dengan log masuk TikTok BacaChat…',
  statusRestricted: '{user} sedang LIVE, tetapi LIVE itu terhad kepada penonton 18+ atau yang log masuk, jadi BacaChat tidak dapat membacanya. Matikan sekatan umur dalam tetapan LIVE, kemudian cuba lagi.',
  statusRestrictedLoggedIn: '{user} sedang LIVE dengan sekatan 18+, dan log masuk TikTok BacaChat tidak dapat membukanya. Matikan sekatan umur dalam tetapan LIVE, kemudian cuba lagi.',
};


const id: Strings = {
  ...en,
  tagline: 'Dengar setiap komentar di LIVE kamu.',
  intro: 'Ketik username TikTok. BacaChat membacakan chat, gift, dan follow begitu masuk.',
  usernameLabel: 'Username TikTok',
  usernamePlaceholder: 'username atau link LIVE',
  start: 'Mulai dengarkan',
  mustBeLive: 'Akun itu harus sedang LIVE sekarang.',
  voiceLanguage: 'Bahasa suara',
  readAloud: 'Bacakan komentar',
  soundFor: 'Suara untuk gift & follow',
  recent: 'Terbaru',
  slotsFree: '{n} dari {max} slot kosong',
  slotsNone: 'Semua {max} slot penuh',
  slotsLoading: 'Memeriksa slot kosong…',
  invalidUsername: 'Itu tidak terlihat seperti username TikTok.',
  language: 'Bahasa',
  appLanguage: 'Bahasa aplikasi',
  detectLanguage: 'Deteksi bahasa tiap komentar',
  detectLanguageSub: 'Ganti suara saat ada yang menulis dalam English atau 中文',
  voice: 'Suara',
  bestVoice: 'Suara terbaik yang tersedia',
  noVoices: 'Tidak ada suara untuk bahasa ini di perangkat ini. Windows: Settings › Time & language › Speech › Add voices. Chrome dan Edge juga punya suara tambahan.',
  speed: 'Kecepatan',
  pitch: 'Nada',
  volume: 'Volume',
  normal: 'Normal',
  low: 'Rendah',
  high: 'Tinggi',
  testVoice: 'Tes suara',
  testSentence: 'Halo, seperti inilah komentar akan terdengar.',
  readSection: 'Bacakan',
  chatComments: 'Komentar chat',
  gifts: 'Gift',
  newFollows: 'Follower baru',
  shares: 'Share',
  viewersJoining: 'Penonton masuk',
  likes: 'Like',
  formatLabel: 'Cara komentar dibaca',
  formatHelp: 'Pakai {comment} untuk pesan dan {nickname} untuk nama penulis, mis. {nickname} bilang {comment}.',
  soundEffects: 'Efek suara',
  sfxGift: 'Gift',
  sfxFollow: 'Follow',
  sfxShare: 'Share',
  sfxChat: 'Chat baru (sebelum dibaca)',
  sfxNames: { coin: 'Bunyi koin', bell: 'Lonceng', pop: 'Pop', whoosh: 'Whoosh', tick: 'Tik', off: 'Mati' },
  preview: 'Dengar suara {name}',
  filters: 'Filter',
  skipEmoji: 'Lewati komentar emoji saja',
  skipLinks: 'Lewati komentar yang berisi link',
  dedupe: 'Jangan ulangi komentar yang sama',
  maxLength: 'Komentar terpanjang yang dibaca',
  characters: '{n} karakter',
  noLimit: 'Tanpa batas',
  blocklist: 'Jangan baca kata-kata ini',
  blocklistPlaceholder: 'Pisahkan dengan koma',
  prefix: 'Hanya baca komentar yang diawali',
  prefixPlaceholder: 'Opsional, mis. !',
  queue: 'Antrean',
  queueMax: 'Komentar maksimum menunggu dibaca',
  queueOption: '{n} — lalu lewati yang paling lama',
  queueHelp: 'LIVE yang ramai lebih cepat daripada suara bisa membaca. Antrean pendek menjaga yang kamu dengar dekat dengan yang di layar.',
  overlay: 'Overlay OBS',
  overlayHelp:
    'Tambahkan URL ini sebagai Browser Source di OBS atau TikTok Live Studio untuk tampilan chat transparan. Overlay ini bisu: untuk memasukkan suara ke stream, capture tab browser ini sebagai sumber audio.',
  copy: 'Salin',
  copied: 'Tersalin',
  back: 'Kembali',
  settings: 'Suara & bunyi',
  bigText: 'Pembaca teks besar',
  compact: 'Kembali ke tampilan ringkas',
  stopAndBack: 'Berhenti dan kembali',
  viewers: 'Penonton',
  follows: 'Follow',
  readingAloud: 'SEDANG DIBACA',
  paused: 'DIJEDA',
  waiting: 'MENUNGGU CHAT',
  voiceOff: 'SUARA MATI',
  inQueue: '{n} menunggu',
  pause: 'Jeda',
  resume: 'Lanjut',
  skip: 'Lewati',
  readAgain: 'Baca lagi',
  smaller: 'Teks lebih kecil',
  bigger: 'Teks lebih besar',
  voiceOn: 'Suara aktif',
  voiceOffShort: 'Suara mati',
  soundsOn: 'Bunyi aktif',
  soundsOff: 'Bunyi mati',
  all: 'Semua',
  chats: 'Chat',
  giftsLikes: 'Gift & like',
  followsTab: 'Follow',
  upNext: 'Berikutnya',
  justRead: 'Baru dibaca',
  watching: 'menonton',
  nothingYet: 'Menunggu komentar pertama…',
  connecting: 'Menghubungkan…',
  connected: 'Terhubung',
  offline: 'Tidak LIVE',
  ended: 'LIVE berakhir',
  statusOffline: '{user} sedang tidak LIVE. Mulai LIVE dulu, lalu coba lagi.',
  statusEnded: 'LIVE sudah berakhir.',
  statusKicked: 'Slot kamu diberikan ke streamer prioritas. BacaChat akan mencoba lagi otomatis.',
  statusFull: 'Semua {max} slot sedang dipakai. Mencoba lagi dalam {s}d…',
  statusBusy: 'BacaChat sedang sibuk. Mencoba lagi dalam {s}d…',
  statusIpLimit: 'Terlalu banyak stream dari koneksi ini (maksimum {max}).',
  statusError: 'Ada masalah: {message}',
  statusReconnecting: 'Koneksi terputus. Menghubungkan ulang dalam {s}d…',
  retryNow: 'Coba sekarang',
  tryAgain: 'Coba lagi',
  giftSpeech: '{nickname} mengirim {count} {gift}',
  followSpeech: '{nickname} sudah follow',
  shareSpeech: '{nickname} membagikan LIVE kamu',
  joinSpeech: '{nickname} masuk',
  likeSpeech: '{nickname} mengirim {count} like',
  giftText: 'mengirim {gift} ×{count}',
  followText: 'follow host',
  shareText: 'membagikan LIVE ini',
  joinText: 'masuk LIVE',
  joinManyText: '{n} penonton masuk',
  likeText: 'mengirim {count} like',
  overlayUsesSlot: 'Catatan: overlay juga memakai satu slot.',
  deviceVoices: 'Suara perangkat',
  more: 'Lainnya…',
  autoUnlock: 'Ketuk di mana saja sekali agar browser mengizinkan suara.',
  changeVoiceLanguage: 'Ganti bahasa suara',
  speakingIn: 'Dibacakan dalam {lang}',
  detectedLanguage: 'Terdeteksi dari komentar',
  edgeHint: 'Untuk suara {lang} yang lebih natural, buka BacaChat di Microsoft Edge. Edge punya suara neural gratis yang terdengar hampir seperti orang asli.',
  dismiss: 'Tutup',
  speedUpBusy: 'Baca lebih cepat saat ramai',
  speedUpBusySub: 'Hingga 30% lebih cepat saat komentar menumpuk',
  premiumVoice: 'Suara premium (ElevenLabs)',
  premiumVoiceLeft: 'Sisa {n} kredit bulan ini',
  premiumVoiceUsedUp: 'Sedang habis, jadi suara perangkat yang membacakan.',
  premiumVoiceNote: 'Hanya untuk streamer prioritas. Otomatis kembali ke suara perangkat.',
  restricted: 'LIVE dibatasi',
  statusLoginExpired: 'LIVE {user} dibatasi 18+, dan login TikTok BacaChat sudah kedaluwarsa, jadi belum bisa dibuka. Minta admin BacaChat memperbarui login, atau matikan batasan usia di pengaturan LIVE.',
  killfeed: 'Banner komentar (killfeed)',
  killfeedHelp: 'Menampilkan komentar terbaru dengan foto profil dan nama selama beberapa detik, transparan selebihnya. Di TikTok LIVE Studio atau OBS, tambahkan sebagai sumber link atau browser dengan resolusi kustom 560 × 200. Tambahkan &demo=1 untuk pratinjau sebelum LIVE.',
  retryingIn: 'Cek lagi dalam {s}d (percobaan {n} dari {max}).',
  retryGaveUp: 'Berhenti mengecek setelah {max} percobaan. Ketuk Coba lagi saat LIVE sudah mulai.',
  statusOpeningRestricted: 'LIVE {user} dibatasi 18+. Membukanya dengan login TikTok BacaChat…',
  statusRestricted: '{user} sedang LIVE, tapi LIVE-nya dibatasi untuk penonton 18+ atau yang sudah login, jadi BacaChat tidak bisa membacanya. Matikan batasan usia di pengaturan LIVE, lalu coba lagi.',
  statusRestrictedLoggedIn: '{user} sedang LIVE dengan batasan 18+, dan login TikTok BacaChat tidak bisa membukanya. Matikan batasan usia di pengaturan LIVE, lalu coba lagi.',
};

const dict: Record<AppLang, Strings> = { en, ms, id };

export function fmt(template: string, vars: Record<string, string | number>): string {
  return template.replace(/\{(\w+)\}/g, (m, k: string) => (k in vars ? String(vars[k]) : m));
}

export function strings(lang: AppLang): Strings {
  return dict[lang] ?? en;
}

export function useT(): Strings {
  const s = useSettings();
  return strings(s.appLang);
}

export const APP_LANGS: Array<{ value: AppLang; label: string }> = [
  { value: 'id', label: 'Bahasa Indonesia' },
  { value: 'ms', label: 'Bahasa Melayu' },
  { value: 'en', label: 'English' },
];
